import { useCallback, useState } from 'react';
import { newIdempotencyKey } from '../api/client';
import type { AppConfiguration, ConfigurationPatch, Definition, Problem } from '../api/types';
import { Badge } from '../components/Badge';
import { Button } from '../components/Button';
import { Card } from '../components/Card';
import { EmptyState } from '../components/EmptyState';
import { ProblemNotice } from '../components/ProblemNotice';
import { Spinner } from '../components/Spinner';
import { Table } from '../components/Table';
import { Tabs } from '../components/Tabs';
import { ConfigForm, missingRequiredConfig } from '../forms/ConfigForm';
import { MicroappFrame } from '../microapp/MicroappFrame';
import { parseManifest } from '../microapp/manifest';
import { Link } from '../app/Link';
import { usePlatform } from '../app/platform';
import { toProblem, useAsync, type AsyncState } from '../app/useAsync';
import { useDraftReview } from '../app/useDraftReview';
import { usePlatformEvents } from '../app/usePlatformEvents';
import { appHeadChipView, appStatusView, runOutcomeLabel, runStatusView } from '../app/status';
import { href, type AppRoute, type AppTab, type DetailPanel, type DmnView, type Route } from '../app/router';
import { CodeTab } from './CodeTab';
import { ConnectionsTab } from './ConnectionsTab';
import { DecisionsTab } from './DecisionsTab';
import { DeleteDialog } from './DeleteDialog';
import { ProcessTab } from './ProcessTab';
import { ReviewTab } from './ReviewTab';
import { formatDateTime } from '../lib/datetime';

export interface AppPageProps {
  route: AppRoute;
  navigate(route: Route, options?: { replace?: boolean }): void;
}

const TAB_LABELS: Array<{ key: AppTab; label: string }> = [
  { key: 'client', label: '画面' },
  { key: 'process', label: 'プロセス' },
  { key: 'decisions', label: '判定' },
  { key: 'code', label: 'コード' },
  { key: 'config', label: '設定' },
  { key: 'connections', label: '接続先' },
  { key: 'runs', label: '実行履歴' },
  { key: 'review', label: 'レビュー' },
];

/** microappの削除で消えるもの。openapi.yaml の deleteApp の説明に合わせる。 */
const APP_DELETION_CONSEQUENCES = [
  'engine配備（BPMN/DMN定義・タイマーやメッセージ開始の待ち受け・実行中のインスタンス）',
  '保存ファイル（アップロードしたZIP・ビルド成果物・asset）',
  'このmicroappが作った業務テーブルとその中のデータ',
  '実行履歴・Task・設定',
];

/** microapp削除の失敗のうち、この画面でしか起きないものの読み方。APIのdetailは英語のまま届く。 */
function appDeletionFailure(problem: Problem): string | null {
  if (problem.code === 'upload_in_progress') {
    return 'このmicroappへのZIP登録が進行中のため削除できません。登録が終わってから、もう一度削除してください。';
  }
  if (problem.code === 'app_tables_referenced') {
    return 'このmicroappの業務テーブルが、同じWorkspaceの他のmicroappから外部キーで参照されています（参照関係は上の詳細のとおり）。参照している側から参照を外してから、もう一度削除してください。';
  }
  if (problem.status === 503) {
    return 'engineに到達できず、削除を途中で止めました。microappは「配備中」のまま残っています。時間をおいてもう一度削除すると、続きから完了します。';
  }
  return null;
}

export function AppPage({ route, navigate }: AppPageProps) {
  const { api, mode } = usePlatform();
  const { workspaceId, appId } = route;
  // 削除の確認に使う名前。開いた時点で固定し、SSEでappを取り直してもダイアログを閉じないようにする。
  const [deleteConfirmName, setDeleteConfirmName] = useState<string | null>(null);

  const app = useAsync(() => api.getApp(workspaceId, appId), [api, workspaceId, appId]);
  // Draft がある app だけ、見出しに Studio と同じ状態チップを出す（G24）。404 は notFound になり何も出ない。
  const reviewState = useDraftReview({ api, workspaceId, appId, enabled: mode === 'http' });
  const chip = appHeadChipView(reviewState.review);
  // Draft だけの microapp（status が draft）は現行パッケージを持たず、定義の取得は 503 app_unavailable になる
  // （Workspace の一覧が draft を詳細画面へ飛ばさないのと同じ理由）。Studio の「版の履歴で見る」はこの画面の
  // コードタブへ直接来るので、app の状態が分かるまで待ち、draft なら定義を読まない。
  const appStatus = app.data?.status ?? null;
  const definition = useAsync<Definition | null>(
    () => (appStatus === null || appStatus === 'draft' ? Promise.resolve(null) : api.getDefinition(workspaceId, appId)),
    [api, workspaceId, appId, appStatus],
  );
  const configuration = useAsync(
    () => api.getAppConfiguration(workspaceId, appId),
    [api, workspaceId, appId],
  );

  // 設定と配備の通知はappId単位で届く。値は含まれないので受信後にsnapshotを取り直す。
  const onEvent = useCallback(
    (event: { type: string }) => {
      if (event.type === 'config.updated') configuration.reload();
      if (event.type === 'app.updated') {
        app.reload();
        definition.reload();
      }
    },
    [app, definition, configuration],
  );
  // 設定が取れなくても配備の通知は必要なので、cursorが無ければ接続時点以後で購読する。
  usePlatformEvents({
    api,
    workspaceId,
    filter: { appId },
    cursor: configuration.data?.eventCursor ?? null,
    enabled: !configuration.loading,
    onEvent,
  });

  const manifest = parseManifest(definition.data?.manifest ?? null, definition.data?.declarations ?? null);
  const missingConfig = configuration.data ? missingRequiredConfig(configuration.data) : [];
  const setRoute = (patch: Partial<AppRoute>) =>
    navigate({ ...route, ...patch }, patch.tab ? undefined : { replace: true });

  return (
    <div className="page app-page">
      <header className="app-heading">
        <div>
          <h1>{app.data?.name ?? appId}</h1>
          <p className="app-subtitle">
            <code>{app.data?.appKey ?? ''}</code>
            {app.data && (
              <>
                {' '}
                <Badge tone={appStatusView(app.data.status).tone}>
                  {appStatusView(app.data.status).label}
                </Badge>
                {chip !== null && (
                  <>
                    {' '}
                    <Badge tone={chip.tone}>{chip.label}</Badge>
                  </>
                )}
              </>
            )}
          </p>
          {app.data && appStatusView(app.data.status).hint && (
            <p className="hint">{appStatusView(app.data.status).hint}</p>
          )}
        </div>
        <div className="app-heading-actions">
          <Link to={{ name: 'workspace', workspaceId }} onNavigate={navigate}>
            Workspaceへ戻る
          </Link>
          <Button
            variant="danger"
            disabled={!app.data}
            onClick={() => {
              if (!app.data) return;
              // manifestのnameは空文字を禁じていないので、空ならappKeyで確認させる。
              setDeleteConfirmName(app.data.name.trim() || app.data.appKey);
            }}
          >
            microappを削除
          </Button>
        </div>
      </header>

      {deleteConfirmName !== null && (
        <DeleteDialog
          kind="microapp"
          name={deleteConfirmName}
          consequences={APP_DELETION_CONSEQUENCES}
          note="同じWorkspaceの他のmicroappと、その業務テーブルは削除しません。"
          describeFailure={appDeletionFailure}
          onDelete={async (idempotencyKey) => {
            try {
              await api.deleteApp(workspaceId, appId, { idempotencyKey });
            } catch (error) {
              // 途中で止まった削除はappを「配備中」のまま残す。見出しの状態を取り直してから失敗を見せる。
              app.reload();
              throw error;
            }
            // 消したappのURLへ「戻る」で帰れないよう履歴を置き換える。
            // Workspace詳細は開くたびにmicroapp一覧を取り直すので、消したappはもう出ない。
            navigate({ name: 'workspace', workspaceId }, { replace: true });
          }}
          onClose={() => setDeleteConfirmName(null)}
        />
      )}

      <ProblemNotice problem={app.problem} action={<Button onClick={app.reload}>再取得</Button>} />
      <ProblemNotice problem={definition.problem} />

      {missingConfig.length > 0 && (
        <MissingConfigNotice
          keys={missingConfig}
          to={{ ...route, tab: 'config' }}
          navigate={navigate}
        />
      )}

      <Tabs
        label="microappの表示"
        active={route.tab}
        onSelect={(key) => setRoute({ tab: key as AppTab })}
        // hrefを渡してタブを実リンクにする。中クリックやコピーでURLをそのまま扱えるようにするため。
        items={TAB_LABELS.map((tab) => ({
          key: tab.key,
          label: tab.label,
          href: href({ ...route, tab: tab.key }),
        }))}
      />

      {manifest.problems.length > 0 && (
        <Card title="manifestに読めない箇所があります">
          <ul className="warnings">
            {manifest.problems.map((problem) => (
              <li key={problem}>{problem}</li>
            ))}
          </ul>
        </Card>
      )}

      {definition.loading && <Spinner label="定義を取得しています" />}

      {definition.data && route.tab === 'process' && (
        <ProcessTab
          workspaceId={workspaceId}
          appId={appId}
          definition={definition.data}
          manifest={manifest}
          appReady={app.data?.status === 'ready'}
          processKey={route.process ?? null}
          elementId={route.element ?? null}
          panel={route.panel ?? 'detail'}
          onSelectProcess={(processKey) => setRoute({ process: processKey, element: undefined })}
          onSelectElement={(element) => setRoute({ element: element ?? undefined })}
          onSelectPanel={(panel: DetailPanel) => setRoute({ panel })}
          onOpenDecision={(decision) => navigate({ ...route, tab: 'decisions', decision })}
          onRunStarted={(run) => navigate({ name: 'run', workspaceId, appId, runId: run.id })}
        />
      )}

      {definition.data && route.tab === 'decisions' && (
        <DecisionsTab
          definition={definition.data}
          decisionKey={route.decision ?? null}
          view={route.view ?? 'table'}
          onSelectDecision={(decision) => setRoute({ decision })}
          onSelectView={(view: DmnView) => setRoute({ view })}
        />
      )}

      {route.tab === 'client' && (
        <ClientTab workspaceId={workspaceId} appId={appId} hasUi={app.data?.hasUi ?? null} />
      )}

      {route.tab === 'code' && (
        <CodeTab
          workspaceId={workspaceId}
          appId={appId}
          file={route.file ?? null}
          appUpdatedAt={app.data?.updatedAt ?? null}
          onSelectFile={(file) => setRoute({ file })}
          codePanel={route.codePanel}
          revision={route.revision ?? null}
          diffView={route.diffView ?? 'file'}
          // ファイル / 版の履歴を切り替えたら、前の表示で選んでいた版と視点は持ち越さない。
          onSelectCodePanel={(codePanel) =>
            setRoute({ codePanel: codePanel ?? undefined, revision: undefined, diffView: undefined })
          }
          onSelectRevision={(revision) => setRoute({ revision: revision ?? undefined })}
          onSelectDiffView={(diffView) => setRoute({ diffView })}
        />
      )}

      {route.tab === 'config' && (
        <ConfigTab
          workspaceId={workspaceId}
          appId={appId}
          editable={app.data?.status === 'ready'}
          configuration={configuration}
        />
      )}

      {route.tab === 'connections' && (
        <ConnectionsTab workspaceId={workspaceId} appId={appId} editable={app.data?.status === 'ready'} />
      )}

      {route.tab === 'runs' && (
        <RunsTab workspaceId={workspaceId} appId={appId} navigate={navigate} />
      )}

      {route.tab === 'review' && (
        <ReviewTab
          workspaceId={workspaceId}
          appId={appId}
          appName={app.data?.name ?? appId}
          // 指摘のファイルを Draft のコードタブで開く。タブを移るので履歴に積む。
          // コードタブの版の履歴・版・視点は持ち越さない（持ち越すとファイルではなく版の履歴に着地する）。
          onOpenFile={(file) =>
            navigate({ ...route, tab: 'code', file, codePanel: undefined, revision: undefined, diffView: undefined })
          }
          navigate={navigate}
        />
      )}
    </div>
  );
}

/**
 * requiredなのに値が無い設定の警告。基盤は登録も実行も止めないので、どのタブでも見える所で知らせる。
 * 公開を止める等の強制は後続（今は警告だけ）。
 */
function MissingConfigNotice({
  keys,
  to,
  navigate,
}: {
  keys: string[];
  to: AppRoute;
  navigate(route: Route): void;
}) {
  return (
    <div className="problem problem-notice" role="status">
      <div className="problem-head">
        <strong className="problem-title">必須の設定が未設定です</strong>
        <div className="problem-action">
          <Link to={to} onNavigate={navigate}>
            設定を開く
          </Link>
        </div>
      </div>
      <p className="problem-detail">
        {keys.map((key, index) => (
          <span key={key}>
            {index > 0 && '、'}
            <code>{key}</code>
          </span>
        ))}
        には既定値がありません。値を入れるまで、この設定を読む処理は失敗します。
      </p>
    </div>
  );
}

function ClientTab({
  workspaceId,
  appId,
  hasUi,
}: {
  workspaceId: string;
  appId: string;
  /** app詳細が未取得のときはnull。404の意味を断定しないために使う。 */
  hasUi: boolean | null;
}) {
  const { api } = usePlatform();
  const client = useAsync(() => api.getClient(workspaceId, appId), [api, workspaceId, appId]);
  const [reloadKey, setReloadKey] = useState(0);

  if (client.loading) return <Spinner label="画面を取得しています" />;
  // ui未宣言のmicroappは404が正常系。それ以外の404は原因を隠さずproblemとして出す。
  if (client.problem?.status === 404 && hasUi === false) {
    return (
      <EmptyState
        title="このmicroappには画面がありません"
        description="BPMNの実行詳細と標準フォームから利用できます。"
      />
    );
  }
  if (client.problem) return <ProblemNotice problem={client.problem} />;
  if (!client.data) return null;

  return (
    <Card
      title="microappの画面"
      actions={
        <Button onClick={() => setReloadKey((value) => value + 1)}>frameを再読込</Button>
      }
    >
      <p className="hint">
        この画面はsandbox iframe内で動き、基盤が検証した操作だけをShell経由で実行します。
      </p>
      <MicroappFrame
        api={api}
        workspaceId={workspaceId}
        appId={appId}
        client={client.data.bundle}
        clientDigest={client.data.etag}
        title="microappの画面"
        reloadKey={reloadKey}
        onHostEvent={(event) => {
          // client artifactが入れ替わったら古いframeを捨てて張り直す。
          if (event.type === 'stale-client') {
            client.reload();
            setReloadKey((value) => value + 1);
          }
        }}
      />
    </Card>
  );
}

function ConfigTab({
  workspaceId,
  appId,
  editable,
  configuration,
}: {
  workspaceId: string;
  appId: string;
  editable: boolean;
  configuration: AsyncState<AppConfiguration>;
}) {
  const { api } = usePlatform();
  const [submitting, setSubmitting] = useState(false);
  const [problem, setProblem] = useState<Problem | null>(null);

  const submit = async (patch: ConfigurationPatch) => {
    setSubmitting(true);
    setProblem(null);
    try {
      await api.updateAppConfiguration(workspaceId, appId, patch, {
        idempotencyKey: newIdempotencyKey(),
      });
      configuration.reload();
    } catch (error) {
      setProblem(toProblem(error));
    } finally {
      setSubmitting(false);
    }
  };

  if (configuration.loading) return <Spinner label="設定を取得しています" />;
  if (configuration.problem) return <ProblemNotice problem={configuration.problem} />;
  if (!configuration.data) return null;

  return (
    <ConfigForm
      configuration={configuration.data}
      editable={editable}
      submitting={submitting}
      problem={problem}
      onSubmit={(patch) => void submit(patch)}
    />
  );
}

function RunsTab({
  workspaceId,
  appId,
  navigate,
}: {
  workspaceId: string;
  appId: string;
  navigate(route: Route): void;
}) {
  const { api } = usePlatform();
  const runs = useAsync(() => api.listRuns(workspaceId, appId), [api, workspaceId, appId]);

  if (runs.loading) return <Spinner label="実行履歴を取得しています" />;
  if (runs.problem) {
    return <ProblemNotice problem={runs.problem} action={<Button onClick={runs.reload}>再取得</Button>} />;
  }
  // 取得できていない状態を「0件」と断定しない。
  if (!runs.data) return null;
  if (runs.data.items.length === 0) return <EmptyState title="実行履歴がありません" />;

  return (
    <Table
      label="実行履歴"
      columns={[
        { key: 'id', header: '実行ID' },
        { key: 'process', header: 'プロセス' },
        { key: 'trigger', header: '起動' },
        { key: 'status', header: '状態' },
        { key: 'outcome', header: '終了' },
        { key: 'createdAt', header: '開始' },
      ]}
      rows={runs.data.items.map((run) => {
        const status = runStatusView(run.status);
        return {
          key: run.id,
          cells: {
            id: (
              <Link to={{ name: 'run', workspaceId, appId, runId: run.id }} onNavigate={navigate}>
                <code>{run.id}</code>
              </Link>
            ),
            process: <code>{run.processKey}</code>,
            trigger: run.trigger,
            status: <Badge tone={status.tone}>{status.label}</Badge>,
            outcome: runOutcomeLabel(run),
            createdAt: formatDateTime(run.createdAt),
          },
        };
      })}
    />
  );
}
